import { useMediaQuery, useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Typography from '@rentspree/component-2023.components.atoms.typography'
import dynamic from 'next/dynamic'
import { useContext, useRef } from 'react'

import { ReviewModePanel } from 'components/common/organisms/review-mode-panel'
import { Footer } from 'containers'
import { ViewModeContext } from 'context/viewModeContext'
import { useSelector } from 'store'
import {
  selectInUnitAmenities,
  selectSharedAmenities,
  selectPublicListingPhotos,
  selectParkingAmenities,
  selectIncludedWithRent,
  selectPublicListingFeatures,
  selectPublicListingPropertyDetails,
  selectPublicListingContactDetails,
  selectPublicListingLeasingInformation,
  selectPublicListingDescription,
  selectPublicListingLocation,
  selectPublicListingGeneratedAddress,
  selectPublicListingAddressDetails,
  selectPublicListingVideo,
  selectPublicListingPartnerName,
} from 'store/public-listing'

import Amenities from './amenities'
import { MAIN_PHOTO_HEIGHT } from './gallery/constants'
import { Header } from './header'
import Location from './location'
import Overview from './overview'
import Policies from './policies'
import PropertyFeatures from './property-features'
import RentalDetails from './rental-details'
import TitleSection from './title-section'
import Widget from './widget'

interface PublicListingProps {
  pageContainerRef: React.RefObject<HTMLDivElement>
}

const Gallery = dynamic(() => import('./gallery'), {
  ssr: false,
  // Reserve the gallery's fixed height while the client-only chunk loads so its
  // late mount doesn't push listing content down (CLS). See TECH-43209. [AC-1]
  // Plain <div> (not MUI Box) so the spacer has zero Emotion runtime/class overhead.
  loading: () => <div style={{ minHeight: MAIN_PHOTO_HEIGHT }} />,
})

export const PublicListing: React.FC<PublicListingProps> = ({ pageContainerRef }) => {
  const listingFeatures = useSelector(selectPublicListingFeatures)
  const inUnitAmenity = useSelector(selectInUnitAmenities)
  const sharedAmenity = useSelector(selectSharedAmenities)
  const photos = useSelector(selectPublicListingPhotos)
  const video = useSelector(selectPublicListingVideo)
  const parkingAmenity = useSelector(selectParkingAmenities)
  const includedWithRent = useSelector(selectIncludedWithRent)
  const propertyDetails = useSelector(selectPublicListingPropertyDetails)
  const leasingInformation = useSelector(selectPublicListingLeasingInformation)
  const contactDetails = useSelector(selectPublicListingContactDetails)
  const description = useSelector(selectPublicListingDescription)
  const position = useSelector(selectPublicListingLocation)
  const { isPreview, isReview } = useContext(ViewModeContext)
  const address = useSelector(selectPublicListingGeneratedAddress)
  const addressDetails = useSelector(selectPublicListingAddressDetails)
  const partnerName = useSelector(selectPublicListingPartnerName)

  const isPreviewOrReviewMode = isPreview || isReview

  const overviewRef = useRef<HTMLDivElement>(null)
  const rentalDetailsRef = useRef<HTMLDivElement>(null)
  const amenitiesRef = useRef<HTMLDivElement>(null)
  const neighborhoodRef = useRef<HTMLDivElement>(null)

  const tabs = [
    {
      label: 'Overview',
      value: 'overview',
      isAvailable: !!description,
      ref: overviewRef,
    },
    {
      label: 'Rental details',
      value: 'rental-details',
      isAvailable: !!leasingInformation,
      ref: rentalDetailsRef,
    },
    {
      label: 'Amenities',
      value: 'amenities',
      isAvailable: !!inUnitAmenity || !!sharedAmenity || !!parkingAmenity,
      ref: amenitiesRef,
    },
    {
      label: 'About the neighborhood',
      value: 'about-the-neighborhood',
      isAvailable: !!position,
      ref: neighborhoodRef,
    },
  ]

  // eslint-disable-next-line no-alert
  const handleApply = () => alert('Apply clicked')
  // eslint-disable-next-line no-alert
  const handleRequestTour = () => alert('Request a tour clicked')

  const widgetProps = {
    contactDetails,
    onApply: handleApply,
    onRequestTour: handleRequestTour,
    isDisabledContact: isPreviewOrReviewMode,
    isDisabledApply: isPreviewOrReviewMode,
    isDisabledRequestTour: isPreviewOrReviewMode,
    isPreview,
  }

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  return (
    <Box
      ref={pageContainerRef}
      className={`public-listing-page-container ${isPreview ? 'no-padding-top' : ''}`}
    >
      {isMobile && isReview && <ReviewModePanel />}
      <Header
        addressDetails={addressDetails}
        isDisabledShare={isPreviewOrReviewMode}
        tabs={tabs}
        isPreview={isPreview}
      />
      {!isMobile && isReview && <ReviewModePanel />}
      {/* Plain <div> with inline minHeight reserves the gallery's space in the
          raw SSR HTML so the client-only (ssr:false) gallery mount does not shift
          content below it — zero dependency on MUI/Emotion runtime. [AC-1] */}
      <div data-testid="gallery-reserve" style={{ minHeight: MAIN_PHOTO_HEIGHT }}>
        <Gallery photos={photos} />
      </div>
      <Box className="listing-info-container">
        <Box className="listing-info-content">
          <TitleSection propertyDetails={propertyDetails} address={address} isPreview={isPreview} />
          {isMobile && <Widget {...widgetProps} />}
          <Overview ref={overviewRef} video={video} description={description} />
          <RentalDetails
            ref={rentalDetailsRef}
            leasingInformation={leasingInformation}
            includedWithRent={includedWithRent.join(', ')}
          />
          <Amenities
            ref={amenitiesRef}
            inUnit={inUnitAmenity}
            shared={sharedAmenity}
            parking={parkingAmenity}
          />
          <PropertyFeatures features={listingFeatures} />
          <Policies leasingInformation={leasingInformation} />
        </Box>
        {!isMobile && <Widget {...widgetProps} />}
      </Box>
      <Box className="listing-source-container">
        <Typography variant="body-medium">Listing source: {partnerName}</Typography>
      </Box>
      <Box className="listing-location-container">
        <Location ref={neighborhoodRef} isPreview={isPreviewOrReviewMode} position={position} />
      </Box>
      <Footer />
    </Box>
  )
}
